import axios from "axios";
import cx from "classnames";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { log } from "../../commonfunctions/logger";
import { OrderItem } from "../../interfaces/interfaces";
import { updateCurrentOrder } from "../../redux/actions/orders";
import AuthenticationHOC from "../AuthenticationHOC/AuthenticationHOC";
import Modal from "../Modal/Modal";
import { Container, StyledInput, StyledNumberInput } from "../styled/styled-components";
import Button, { ButtonType } from "../Button/Button";
import { Context, IContext } from "../../context/ContextApp";
import Icon from "../Icon/icon";
import { getTotalStock } from "./utils";

interface Sort {
  ascending: boolean;
  type: "index" | "stock" | "b/o" | "none";
}

interface Props {
  generateAuthHeader: any;
  action?: boolean;
  bo?: boolean;
  disableAvailableQty?: boolean;
  index: number | string | false;
  item: OrderItem;
  split?: boolean;
  showStock?: boolean;
  disableTotal?: boolean;
  checkout?: boolean;
  sort?: Sort;
  onSort?: (sort: Sort) => void;
  hideDetailsButton?: boolean;
  qtyColumn?: boolean;
}

const CartItem = (props: Props) => {
  const {
    generateAuthHeader,
    action,
    bo,
    disableAvailableQty,
    index,
    item,
    split,
    showStock,
    disableTotal,
    checkout,
    hideDetailsButton,
    qtyColumn,
  } = props;

  const [editMode, setEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [reference, setReference] = useState(item.reference);
  const [quantity, setQuantity] = useState(item.quantity);

  useEffect(() => {
    setReference(item.reference);
    setQuantity(item.quantity);
    setEditMode(false);
  }, [item.id, item.reference, item.quantity]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { appBranch }: IContext = useContext(Context);
  const isCod = useSelector((state: RootState) => state.userSession.isCod);
  const totalStock = getTotalStock(item.stock);

  const update = () => {
    axios
      .put(
        `${process.env.REACT_APP_HALFBACK_ROOT_API}/orders/current_order/items/update`,
        { item: { id: item.id, quantity, reference, available_stock: totalStock } },
        generateAuthHeader(),
      )
      .then((response) => {
        dispatch(updateCurrentOrder(response.data));
        setEditMode(false);
      })
      .catch((error) => {
        log(error, "Update cart call failed.");
        if (typeof error === "string" && error.includes("401")) {
          alert("Your session has expired, please log in again.");
          window.location.reload();
        }
      });
  };

  const removeItem = () => {
    axios
      .put(
        `${process.env.REACT_APP_HALFBACK_ROOT_API}/orders/current_order/items/delete`,
        { id: item.id },
        generateAuthHeader(),
      )
      .then((response) => {
        dispatch(updateCurrentOrder(response.data));
        setShowModal(false);
      })
      .catch((error) => {
        log(error, "Remove cart item failed.");
        setShowModal(false);
      });
  };

  const getProductImage = () => {
    const images = item.images?.length ? item.images : item.color_images || [];
    const frontImage = images.find((img) => img.front || img.front_image) || images[0];
    return (
      frontImage?.https_attachment_url_product || "https://cdn.fashionbiz.com/Image+Not+Found.jpg"
    );
  };

  const getWarehouseQty = (location: string) =>
    item.warehouses?.find((w) => w.location === location)?.quantity || 0;

  const columnCount =
    4 + // expand, description, color, size
    (bo ? 1 : 0) +
    (!disableAvailableQty ? 1 : 0) +
    1 + // price
    (!disableTotal ? 1 : 0) + // total
    (action ? 1 : 0);

  const showDetails = !qtyColumn && (isExpanded || editMode);

  const handleRowClick = () => {
    if (qtyColumn) return;
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <tr
        className={cx("cart-item", "cart-item--main", {
          "cart-item--expanded": showDetails,
          "cart-item--static": qtyColumn,
          highlight: showModal,
        })}
        role="listitem"
        onClick={handleRowClick}
      >
        {!qtyColumn && (
          <td className="expand-cell">
            <div className="expand-icon-wrapper">
              <Icon name="ArrowDown" iconWidth="1rem" className="expand-icon" />
            </div>
          </td>
        )}

        <td className="description-cell">
          <div className="description-content">
            <img className="previewimage" src={getProductImage()} alt={item.product_name} />
            <div className="description-meta">
              <div>
                <span className="product-name">{item.product_name}</span>
                <br />
                <b className="product-code">{item.product_code || item.itemNo}</b>
                <br />
                <span className="product-sku">SKU {item.sku}</span>
              </div>
            </div>
          </div>
        </td>

        <td className="color-cell">{item.color}</td>
        <td className="size-cell">{item.size}</td>
        {qtyColumn && <td className="qty-cell">{item.quantity}</td>}

        {bo && (
          <td className="bo-cell">
            {item.backorder ? (
              <span className="bo-qty">{item.backorder.quantity}</span>
            ) : (
              <span className="bo-in-stock">In Stock</span>
            )}
          </td>
        )}

        {!disableAvailableQty && (
          <td className="available-qty-cell">
            <div className={cx({ splitQuantity: split })}>{item.available_quantity}</div>
          </td>
        )}

        <td className="price-cell">${parseFloat(item.price).toFixed(2)}</td>
        {!disableTotal && <td className="total-cell">${(item.available_price ?? 0).toFixed(2)}</td>}

        {action && (
          <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
            <Container row>
              <div className="button-container actions-buttons">
                {editMode ? (
                  <>
                    <Button
                      onClick={update}
                      type={ButtonType.primary}
                      style={{ padding: "0.5rem" }}
                    >
                      <Icon name="Check" iconWidth="1.3rem" style={{ fill: "white" }} />
                      <span className="action-btn-label">Save</span>
                    </Button>
                    <Button
                      onClick={() => setEditMode(false)}
                      type={ButtonType.secondary}
                      style={{ padding: "0.5rem" }}
                    >
                      <Icon name="Times" style={{ fill: "black" }} />
                      <span className="action-btn-label">Cancel</span>
                    </Button>
                  </>
                ) : (
                  <>
                    {!hideDetailsButton && (
                      <Button
                        onClick={() => navigate(`/products/${item.brand}/${item.product}`)}
                        type={ButtonType.secondary}
                        className="view-product-btn"
                        style={{ padding: "0.5rem" }}
                      >
                        <Icon name="Eye" iconWidth="1.1rem" />
                        <span className="action-btn-label">View</span>
                      </Button>
                    )}
                    <Button
                      onClick={() => setEditMode(true)}
                      type={ButtonType.secondary}
                      style={{ padding: "0.5rem" }}
                    >
                      <Icon name="EditIcon" />
                      <span className="action-btn-label">Edit</span>
                    </Button>
                    <Button
                      onClick={() => setShowModal(true)}
                      type={ButtonType.danger}
                      style={{ padding: "0.5rem" }}
                    >
                      <Icon name="TrashWhite" style={{ fontWeight: "bold" }} />
                      <span className="action-btn-label">Remove</span>
                    </Button>
                  </>
                )}
              </div>
            </Container>
          </td>
        )}
      </tr>

      {showDetails && (
        <tr className="cart-item--details">
          <td colSpan={columnCount} className="details-wrapper">
            <div className="details-content">
              <div className="details-grid">
                {index && (
                  <div className="detail-item">
                    <span className="detail-label">Line:</span>
                    <span className="detail-value">{index}</span>
                  </div>
                )}

                {(editMode || reference) && (
                  <div className="detail-item">
                    <span className="detail-label">Reference:</span>
                    <span className="detail-value">
                      {editMode ? (
                        <StyledInput
                          style={{ padding: "0 5px" }}
                          defaultValue={reference || ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setReference(e.target.value)
                          }
                          maxLength={30}
                        />
                      ) : (
                        reference
                      )}
                    </span>
                  </div>
                )}

                {["au", "nz"].includes(appBranch) ? (
                  <div className="detail-item">
                    <span className="detail-label">Order Qty:</span>
                    <span className="detail-value">
                      {editMode ? (
                        <StyledNumberInput
                          style={{ width: "80px", textAlign: "center" }}
                          defaultValue={quantity}
                          min="1"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setQuantity(parseInt(e.target.value))
                          }
                          type="number"
                        />
                      ) : (
                        item.quantity
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="detail-item">
                    <span className="detail-label">Qty:</span>
                    <span className="detail-value">{item.quantity}</span>
                  </div>
                )}

                {checkout && appBranch === "au" && split && (
                  <>
                    <div className="detail-item detail-item--region detail-item--syd">
                      <span className="detail-label">SYD:</span>
                      <span className="detail-value">{getWarehouseQty("SYD")}</span>
                    </div>
                    <div className="detail-item detail-item--region detail-item--mel">
                      <span className="detail-label">MEL:</span>
                      <span className="detail-value">{getWarehouseQty("MEL")}</span>
                    </div>
                    <div className="detail-item detail-item--region detail-item--per">
                      <span className="detail-label">PER:</span>
                      <span className="detail-value">{getWarehouseQty("PER")}</span>
                    </div>
                  </>
                )}

                {showStock && (
                  <div className="detail-item">
                    <span className="detail-label">Available Stock:</span>
                    <span
                      className="detail-value"
                      style={{ color: totalStock >= item.quantity ? "green" : "#d90000" }}
                    >
                      {totalStock}
                    </span>
                  </div>
                )}
              </div>

              {appBranch === "nz" && item.enable_ship_from_au === true && (
                <div className="au-transfer-alert">
                  <p className="au-transfer-alert__text">
                    <span className="au-transfer-alert__title">International Transfer:</span>
                    {
                      " This item ships separately from Australia and has been added to your cart as an 'in-stock' item."
                    }
                  </p>
                  {isCod && (
                    <p className="au-transfer-alert__text">
                      <strong>Note:</strong> All international stock transfer items must be paid for
                      in full prior to order processing.
                    </p>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}

      {showModal && (
        <Modal>
          <div className="cart-item-modal-template">
            Are you sure you want to remove this item?
            <Container>
              <Button onClick={removeItem} type={ButtonType.danger} style={{ margin: "1em 0" }}>
                Yes
              </Button>
              <Button onClick={() => setShowModal(false)} type={ButtonType.secondary}>
                No
              </Button>
            </Container>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AuthenticationHOC(CartItem);
